import globalAgent from 'src/agent';
import { URLS } from 'src/constants/urls';

export interface ILeadExample {
    id: number;
    text: string;
    source: string;
    date: string;
    category: string;
    category_icon?: string;
    keywords: string[];
    isSelected?: boolean;
}

export interface INicheAnalysisRequest {
    service_description: string;
    project_goal: string;
    project_id: number; // ID проекта, в который сохранять анализ
    use_geolocation?: boolean;
    selected_locations?: Array<{ name: string; [key: string]: any }>;
}

export interface IRecommendedCollection {
    id: number;
    name: string;
    city?: string | null;
    tags: string[];
    chat_count: number;
}

export interface IDirectForecastQuery {
    query: string;
    intent: 'hot' | 'warm' | 'research' | 'negative' | string;
    monthly_shows_estimate: number;
    clicks_base_estimate: number;
    cpc_base_estimate: number;
    budget_base_estimate: number;
    commercial_score: number;
    source: string;
}

export interface IDirectForecastCluster {
    id: string;
    title: string;
    intent: string;
    phrases_count: number;
    monthly_shows_estimate: number;
    clicks_base_estimate: number;
    budget_base_estimate: number;
    lead_forecast: {
        low: number;
        base: number;
        high: number;
    };
}

export interface IDirectForecastSummary {
    status: string;
    mode: 'model_estimate' | 'wordstat_forecast' | string;
    connection_status: 'not_connected' | 'connected' | 'expired' | 'revoked' | 'error' | string;
    title: string;
    geo: {
        input: string;
        confidence: number;
    };
    summary: {
        monthly_shows_estimate: number;
        monthly_clicks_estimate: number;
        budget_low: number;
        budget_base: number;
        budget_high: number;
        lead_forecast: {
            low: number;
            base: number;
            high: number;
        };
        confidence: string;
    };
    queries: IDirectForecastQuery[];
    clusters: IDirectForecastCluster[];
    negative_phrases: string[];
    strategies: Array<{
        id: string;
        title: string;
        recommended: boolean;
        weekly_budget: number;
        expected_monthly_leads: {
            low: number;
            base: number;
            high: number;
        };
        direct_strategy: string;
        risk: string;
    }>;
    next_action: 'connect_direct_for_wordstat_forecast' | 'run_direct_audit' | string;
    source_meta: {
        source: string;
        generated_at: string;
        note?: string;
    };
    assumptions?: Record<string, number>;
    analysis_id?: number;
}

export interface INicheAnalysisResponse {
    keywords: string[];
    lead_examples: ILeadExample[];
    recommended_collections: {
        city_collections: IRecommendedCollection[];
        general_collections: IRecommendedCollection[];
    };
    location_profile?: {
        mode: 'bounded' | 'unbounded' | 'unknown' | string;
        raw_locations: string[];
        entries: Array<Record<string, any>>;
        city_names: string[];
        region_names: string[];
        country_codes: string[];
        geo_terms: string[];
        address_entries?: Array<Record<string, any>>;
        coordinates?: {
            lat: number | null;
            lng: number | null;
        } | null;
    };
    analysis_summary: string;
    category?: string;
    project_id: number; // ID проекта, в который сохранен анализ
    direct_forecast?: IDirectForecastSummary;
}

class NicheAnalysisService {
    queryKey = 'niche-analysis';

    async analyzeNiche(data: INicheAnalysisRequest): Promise<INicheAnalysisResponse> {
        try {
            const response = await globalAgent.post(URLS.NICHE_ANALYSIS, data);
            return response.data;
        } catch (error) {
            console.error('Ошибка анализа ниши:', error);
            throw error;
        }
    }

    async getLeadExamples(keywords: string[]): Promise<ILeadExample[]> {
        try {
            const response = await globalAgent.get(URLS.LEAD_EXAMPLES, {
                params: { keywords: keywords.join(',') },
            });
            return response.data;
        } catch (error) {
            console.error('Ошибка получения примеров лидов:', error);
            throw error;
        }
    }
}

export default new NicheAnalysisService();
